import React, { useState, useEffect, useMemo, useRef } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { matchPath, useLocation } from "react-router-dom";
import { routeURLs } from "../../utils/enums";
//style
import css from "../genericMagneticStyle.less";

//utils
import { isOverviewPage, setLocalStorageFlag } from "../../utils/common";
import AppHoc from "../AppHOC";
import timeFilterConfig from "../config/time-filter-config";
import {
  isCustom,
  getStoredTimePeriod,
  filterTimeOptions,
  isOptimizationPage,
} from "../../utils/common";
import {
  subtractUnitsFromDate,
  getPeriod,
  prevDay,
  getTimePeriodELT,
  getUTCStartFromDateInput,
  getUTCEndFromDateInput,
  getDayDiffFromDateInputs,
  addDaysToDateInputValue,
  getStartfDayUTCFormat,
  toDateInputValue,
} from "../../utils/displayTime";
import { updateRumCustomTimeRange } from "../../monitoring";
import { updateCookie } from "../../utils/common";
import { getCookie } from "../../serviceWorker";
import LockIcon from "../authorization/LockIcon";
import { timeFilterTypes } from "../../../src/utils/enums";
import { useI18n } from "../../i18n/useI18n";
import { startOfWeek } from "date-fns";

const [
  HbrSelect,
  HbrIcon,
  HbrOption,
  HbrInput,
  HbrPortal,
  HbrTooltip,
  HbrIconButton,
] = reactWrapper([
  "hbr-select",
  "hbr-icon",
  "hbr-option",
  "hbr-input",
  "hbr-portal",
  "hbr-tooltip",
  "hbr-icon-button",
]);

const MAX_CUSTOM_END_DATE_OFFSET_DAYS = 6;
const DATE_INPUT_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const START_DATE_ERROR_KEYS = [
  "timeFilterCustomDateSameError",
  "timeFilterCustomStartDateInvalidError",
];
const END_DATE_ERROR_KEYS = [
  "timeFilterCustomDateEndBeforeStartError",
  "timeFilterCustomDateError",
  "timeFilterCustomDateSameError",
];

const parseDateInputValue = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const getDateInputValue = (event) =>
  event?.target?.value ??
  event?.currentTarget?.value ??
  event?.detail?.value ??
  "";

/**
 * Time Filter
 * Displays time selection options in dropdown,
 * along with timestamp date comparison
 *
 * See the parameter defaults for an example
 *
 * Usage:
 *      <TimeFilter />
 */

const TimeFilter = ({
  globalFilter,
  setCtrlTimeFilter,
  setPathAnalyticsTab,
  setSideBarTimeFilter,
  sideBar = false,
  setCustomTime,
  setSideBarCustomTime,
}) => {
  const { tGenerics } = useI18n("generics");
  const location = useLocation();
  const isCustomDateShown = false;
  const timeFilter = isCustom(globalFilter.timePeriodSelected)
    ? globalFilter.timePeriodSelected
    : getStoredTimePeriod();
  const [customDateValues, setCustomDateValues] = useState({
    start: "",
    end: "",
  });
  const [dateErrorMessageKey, setDateErrorMessageKey] = useState(
    "timeFilterCustomDateError",
  );
  const [timePeriodSelected, setTimePeriodSelected] = useState(
    timeFilter ? timeFilter : "1",
  );
  let lastDisplayDate = prevDay();
  const [dateErrorState, setDateErrorState] = useState(false);
  const preventStoredDateRestoreRef = useRef(false);
  const dateErrorTooltipId = sideBar
    ? "custom-date-error-icon-sidebar"
    : "custom-date-error-icon-main";
  const currentDateErrorTooltipId = `${dateErrorTooltipId}-${dateErrorMessageKey}`;
  const dateErrorMessage = tGenerics(dateErrorMessageKey);
  const isStartDateError =
    dateErrorState && START_DATE_ERROR_KEYS.includes(dateErrorMessageKey);
  const isEndDateError =
    dateErrorState && END_DATE_ERROR_KEYS.includes(dateErrorMessageKey);

  // Set time_period value for Qualtrics metadata
  useEffect(() => {
    window._time_period = timeFilterTypes[globalFilter.timePeriodSelected];
  }, [globalFilter.timePeriodSelected]);

  const availableOptions = useMemo(() => {
    const result = {};
    const { available, enabled } = globalFilter.features;
    for (const key in timeFilterConfig) {
      const value = timeFilterConfig[key];
      if (!value.feature) result[key] = { ...value, disabled: false };
      else if (available.includes(value.feature))
        result[key] = {
          ...value,
          disabled: enabled.includes(value.feature) === false,
        };
    }
    return result;
  }, []);

  useEffect(() => {
    if (isCustom(timePeriodSelected) && document.querySelector(".timestamp"))
      document.querySelector(".timestamp").style.visibility = "hidden";
    if (sideBar) {
      setSideBarTimeFilter(
        {
          current_period: globalFilter.timeFilter.current_period,
          previous_period: globalFilter.timeFilter.previous_period,
        },
        timePeriodSelected,
      );
    }
  }, []);

  useEffect(() => {
    if (dateErrorState) {
      return;
    }

    if (preventStoredDateRestoreRef.current) {
      return;
    }

    if (!globalFilter.selectedCustomTime?.current_period) return;
    const [start, end] = globalFilter.selectedCustomTime.current_period;
    setCustomDateValues({
      start: toDateInputValue(start),
      end: toDateInputValue(end),
    });
  }, [dateErrorState, globalFilter.selectedCustomTime]);

  /****if sitesdropdwon selected to all sites the pa tab set false in reducer  */
  useEffect(() => {
    if (globalFilter.selectedSite === -1) {
      setPathAnalyticsTab(false);
    }
  }, [globalFilter.selectedSite, globalFilter.selectedNhName]);

  const applyCustomTime = (startDate, endDate) => {
    preventStoredDateRestoreRef.current = false;
    const startCustomTimeStamp = getStartfDayUTCFormat(new Date(startDate));
    const endCustomTimeStamp = getStartfDayUTCFormat(new Date(endDate));
    const diffInDays = getDayDiffFromDateInputs(
      toDateInputValue(endDate),
      toDateInputValue(startDate),
    );
    const customRange = {
      start: { date: startCustomTimeStamp },
      end: { date: endCustomTimeStamp },
    };

    const timePeriod = {
      current_period: [startDate, endDate],
      previous_period: [
        getPeriod(endDate, "days", diffInDays * 2, "days"),
        getPeriod(endDate, "days", diffInDays * 24 * 60 + 1, "minutes"),
      ],
    };

    const currTimePeriod = {
      current_period: timePeriod.current_period,
      previous_period: timePeriod.previous_period,
    };
    if (sideBar) {
      setSideBarCustomTime({
        selectedCustomTime: { ...currTimePeriod },
        ...currTimePeriod,
        range: customRange,
        timePeriodSelected: timePeriodSelected,
      });
      setSideBarTimeFilter(timePeriod, timePeriodSelected);
    } else {
      setCustomTime({
        ...currTimePeriod,
        sideBarTimeFilter: { ...currTimePeriod },
        range: customRange,
      });

      setCtrlTimeFilter(timePeriod, timePeriodSelected);
    }
    updateRumCustomTimeRange([
      currTimePeriod.previous_period[0],
      currTimePeriod.current_period[1],
    ]);
  };

  const validateAndApplyDateRange = (startValue, endValue) => {
    if (!startValue) {
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      return;
    }

    if (!endValue) {
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      return;
    }

    if (startValue > endValue) {
      setDateErrorState(true);
      setDateErrorMessageKey("timeFilterCustomDateEndBeforeStartError");
      return;
    }

    if (startValue === endValue) {
      setDateErrorState(true);
      setDateErrorMessageKey("timeFilterCustomDateSameError");
      return;
    }

    const startDateTimestamp = getUTCStartFromDateInput(startValue);
    const endDateTimestamp = getUTCEndFromDateInput(endValue);
    const diff = getDayDiffFromDateInputs(endValue, startValue);
    const isDateValid = diff <= MAX_CUSTOM_END_DATE_OFFSET_DAYS && diff > 0;

    setDateErrorMessageKey("timeFilterCustomDateError");

    setDateErrorState(isDateValid === false);

    if (isDateValid) {
      applyCustomTime(startDateTimestamp, endDateTimestamp);
    }
  };

  const getMaxEndDateValue = (startValue) => {
    if (!startValue) {
      return toDateInputValue(lastDisplayDate);
    }

    const maxAllowedEndDate = addDaysToDateInputValue(
      startValue,
      MAX_CUSTOM_END_DATE_OFFSET_DAYS,
    );
    const maxDisplayDate = toDateInputValue(lastDisplayDate);

    return maxAllowedEndDate > maxDisplayDate
      ? maxDisplayDate
      : maxAllowedEndDate;
  };

  const getMinEndDateValue = (startValue, defaultMinDate) => {
    if (!startValue) {
      return toDateInputValue(defaultMinDate);
    }

    return startValue;
  };

  const isStartDateInvalid = (value, minDate, maxDate) => {
    if (!value || DATE_INPUT_VALUE_PATTERN.test(value) === false) return true;

    const startDate = parseDateInputValue(value);
    if (!startDate) return true;

    return startDate < minDate || startDate > maxDate;
  };

  const syncStartDateErrorFromBase = (event) => {
    const hbrInput = event.currentTarget;

    requestAnimationFrame(() => {
      // Keep existing end-date errors visible when focus returns to start date.
      if (
        customDateValues.end &&
        END_DATE_ERROR_KEYS.includes(dateErrorMessageKey)
      ) {
        return;
      }

      // Read the native input value from Harbor's shadow DOM when available.
      const input = hbrInput.shadowRoot?.querySelector("input");
      const value =
        input?.value ?? hbrInput.value ?? hbrInput.getAttribute("value") ?? "";

      // Mirror native date-input validity into the component error state.
      const hasStartInputError = Boolean(
        input?.validity?.badInput ||
        (value &&
          (input?.validity?.rangeUnderflow ||
            input?.validity?.rangeOverflow ||
            !DATE_INPUT_VALUE_PATTERN.test(value))),
      );

      // Show the start-date error only while the range is still incomplete.
      if (hasStartInputError && !customDateValues.end) {
        setDateErrorState(true);
        setDateErrorMessageKey("timeFilterCustomStartDateInvalidError");
        return;
      }

      // Clear stale start-date errors after the base input becomes valid again.
      if (dateErrorMessageKey === "timeFilterCustomStartDateInvalidError") {
        setDateErrorState(false);
        setDateErrorMessageKey("timeFilterCustomDateError");
      }
    });
  };

  const formatDateMessageValue = (date) => {
    const value = toDateInputValue(date);
    if (!value) return "";

    const [year, month, day] = value.split("-");
    return `${day}-${month}-${year}`;
  };

  const onTimeChange = (e) => {
    const targetValue = e.currentTarget.value;
    const timePeriod = getTimePeriodELT(targetValue);

    // For Qualtrics Metadata
    window._time_period = timeFilterTypes[targetValue];

    setTimePeriodSelected(targetValue);

    if (!isCustom(targetValue) && !sideBar) {
      setLocalStorageFlag("timePeriodSelected", targetValue);
      updateCookie("timePeriodSelected", targetValue);
    }

    if (!isCustom(targetValue)) {
      preventStoredDateRestoreRef.current = false;
      if (document.querySelector(".timestamp"))
        document.querySelector(".timestamp").style.visibility = "visible";
      setCustomDateValues({ start: "", end: "" });
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      sideBar
        ? setSideBarTimeFilter(timePeriod, targetValue)
        : setCtrlTimeFilter(timePeriod, targetValue);
      updateRumCustomTimeRange(null);
    } else {
      if (document.querySelector(".timestamp"))
        document.querySelector(".timestamp").style.visibility = "hidden";

      setCustomDateValues({ start: "", end: "" });
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      sideBar
        ? setSideBarTimeFilter(null, targetValue)
        : setCtrlTimeFilter(timePeriod, targetValue);
    }
  };

  const onStartDateChange = (event) => {
    const startValue = getDateInputValue(event);

    if (!startValue) {
      preventStoredDateRestoreRef.current = true;
      setCustomDateValues({ start: "", end: "" });
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      return;
    }

    const today = lastDisplayDate;
    const curMinDate = startOfWeek(subtractUnitsFromDate(today, 11, "weeks"), {
      weekStartsOn: 0,
    });

    const previousEndValue = customDateValues.end;
    setCustomDateValues({
      start: startValue,
      end: previousEndValue,
    });

    if (isStartDateInvalid(startValue, curMinDate, today)) {
      preventStoredDateRestoreRef.current = true;
      setCustomDateValues({
        start: startValue,
        end: "",
      });
      setDateErrorState(true);
      setDateErrorMessageKey("timeFilterCustomStartDateInvalidError");
      return;
    }

    validateAndApplyDateRange(startValue, previousEndValue);
  };

  const onEndDateChange = (event) => {
    const endValue = getDateInputValue(event);

    if (!endValue) {
      preventStoredDateRestoreRef.current = true;
      setCustomDateValues({
        ...customDateValues,
        end: "",
      });
      setDateErrorState(false);
      setDateErrorMessageKey("timeFilterCustomDateError");
      return;
    }

    preventStoredDateRestoreRef.current = false;
    const nextDateValues = {
      ...customDateValues,
      end: endValue,
    };

    setCustomDateValues(nextDateValues);
    validateAndApplyDateRange(nextDateValues.start, nextDateValues.end);
  };

  const renderFilter = () => {
    let filterOptions = { ...availableOptions };
    // Determine which context is active
    const isSecurityTabPath = matchPath(
      {
        path: routeURLs.threatIntelligence,
      },
      location.pathname,
    );
    const isOptimizationTabPath = matchPath(
      {
        path: routeURLs.optimization,
      },
      location.pathname,
    );
    const app360PathName = location.pathname.includes("/Dashboards");
    const isPathAnalyticsActive =
      globalFilter.pathanalyticsTabView && app360PathName;

    // Using the utility function to filter options
    filterOptions = filterTimeOptions(
      { ...filterOptions },
      {
        isSecurity: isSecurityTabPath,
        isOptimization: isOptimizationTabPath,
        isPathAnalytics: isPathAnalyticsActive,
      },
    );

    // Determine default time selection based on current tab and selection
    const shouldResetCustom =
      (isOptimizationTabPath &&
        ["1", "3", "6", "Custom"].includes(timePeriodSelected)) ||
      ((isSecurityTabPath || isPathAnalyticsActive) &&
        timePeriodSelected === "Custom");

    if (shouldResetCustom) {
      const isUIconvergence = Boolean(getCookie("cl-analytics"));
      const defaultTimeValue =
        isUIconvergence && !isOptimizationPage() ? "1" : "12";
      setTimePeriodSelected(defaultTimeValue);

      const timePeriod = getTimePeriodELT(defaultTimeValue);

      setLocalStorageFlag("timePeriodSelected", defaultTimeValue);
      updateCookie("timePeriodSelected", defaultTimeValue);

      if (sideBar) {
        setSideBarTimeFilter(timePeriod, defaultTimeValue);
      } else {
        setCtrlTimeFilter(timePeriod, defaultTimeValue);
      }
    }

    const today = lastDisplayDate;
    let twelveWeeksBefore = startOfWeek(
      subtractUnitsFromDate(today, 11, "weeks"),
      { weekStartsOn: 0 },
    );
    let curMinDate = twelveWeeksBefore;
    const curMaxDateValue = getMaxEndDateValue(customDateValues.start);
    const curMinEndDateValue = getMinEndDateValue(
      customDateValues.start,
      curMinDate,
    );
    const isStartDateValueInvalid =
      customDateValues.start &&
      isStartDateInvalid(customDateValues.start, curMinDate, today);
    const endInputKey = `custom-date-end-${isEndDateError ? dateErrorMessageKey : "clean"}`;
    const displayedDateErrorMessage =
      dateErrorMessageKey === "timeFilterCustomStartDateInvalidError"
        ? tGenerics(dateErrorMessageKey, {
            minDate: formatDateMessageValue(curMinDate),
            maxDate: formatDateMessageValue(today),
          })
        : dateErrorMessage;
    const hbrDate = (
      <div className="custom-date">
        <HbrInput
          className="custom-date-input"
          type="date"
          data-cy="customDateStart"
          arialabel={tGenerics("startDate")}
          min={toDateInputValue(curMinDate)}
          max={toDateInputValue(today)}
          value={customDateValues.start}
          error={isStartDateError}
          onHbr-input={onStartDateChange}
          onkeyup={syncStartDateErrorFromBase}
          aria={{ invalid: isStartDateError }}
        />
        <HbrInput
          key={endInputKey}
          className="custom-date-input"
          type="date"
          data-cy="customDateEnd"
          arialabel={tGenerics("endDate")}
          min={curMinEndDateValue}
          max={curMaxDateValue}
          value={customDateValues.end}
          error={isEndDateError}
          disabled={!customDateValues.start || isStartDateValueInvalid}
          onHbr-input={onEndDateChange}
          aria={{ invalid: isEndDateError }}
        />
        {dateErrorState && displayedDateErrorMessage && (
          <div className="custom-date-error-icon">
            <span
              className="custom-date-error-message-sr"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              {displayedDateErrorMessage}
            </span>
            <HbrIconButton
              key={`icon-${dateErrorMessageKey}`}
              data-testid="custom-start-date-error-icon"
              name="alerts-negative-large"
              id={currentDateErrorTooltipId}
              sentiment="danger"
              size="24px"
              noPadding={true}
              arialabel={displayedDateErrorMessage}
            />
            <HbrPortal key={`portal-${dateErrorMessageKey}`}>
              <HbrTooltip
                key={`tooltip-${dateErrorMessageKey}`}
                anchor={`#${currentDateErrorTooltipId}`}
              >
                <div slot="content">{displayedDateErrorMessage}</div>
              </HbrTooltip>
            </HbrPortal>
          </div>
        )}
      </div>
    );

    return (
      <>
        <div data-cy="timeFilterDropDown" className="date-filter-dropdown">
          <HbrSelect
            className={
              sideBar ? "time-filter-select" : "time-filter-select-main"
            }
            value={timePeriodSelected}
            onHbr-change={onTimeChange}
            arialabel={tGenerics("timeFilterDuration", {
              label: filterOptions[timePeriodSelected]?.label || "",
            })}
          >
            <HbrIcon slot="prefix" name="clock" sentiment="neutral" />
            {Object.values(filterOptions).map((item) => (
              <HbrOption
                key={item.label}
                value={item.value}
                disabled={item.disabled}
                data-testid={`time-filter_${item.value}`}
              >
                {item.label}
                {item.disabled === true && (
                  <div slot="suffix" className="flex-items">
                    <LockIcon id={`time-filter_${item.value}`} />
                  </div>
                )}
              </HbrOption>
            ))}
          </HbrSelect>
        </div>
        {!isCustomDateShown &&
          !isSecurityTabPath &&
          isCustom(timePeriodSelected) &&
          hbrDate}
      </>
    );
  };

  if (!globalFilter.isTimeFilterHidden) {
    return (
      <div className={css["time-filter"]} data-cy="timeFilter">
        <div className={isOverviewPage() ? "custom-filter" : "filter"}>
          {renderFilter()}
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

TimeFilter.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  sideBar: PropTypes.bool,
  setSideBarTimeFilter: PropTypes.func.isRequired,
  setPathAnalyticsTab: PropTypes.func.isRequired,
  setCtrlTimeFilter: PropTypes.func.isRequired,
  setSideBarCustomTime: PropTypes.func.isRequired,
  setCustomTime: PropTypes.func.isRequired,
};

export default AppHoc(TimeFilter);
